import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { ArrowLeft, Search, MessageCircle, CreditCard, CheckCircle, Star, Users } from 'lucide-react';

const HowToUsePage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const clientSteps = [
    { icon: Search, title: 'Browse Services', desc: 'Explore our catalog of verified service providers' },
    { icon: MessageCircle, title: 'Contact Provider', desc: 'Chat directly to discuss details and pricing' },
    { icon: CreditCard, title: 'Pay Securely', desc: 'Make upfront payment to book the service' },
    { icon: CheckCircle, title: 'Confirm Completion', desc: 'Confirm when service is completed to your satisfaction' },
    { icon: Star, title: 'Rate & Review', desc: 'Leave feedback to help other clients' }
  ];

  const providerSteps = [
    { icon: Users, title: 'Create Profile', desc: 'Set up your professional profile with services offered' },
    { icon: MessageCircle, title: 'Receive Requests', desc: 'Clients will contact you through the platform' },
    { icon: CheckCircle, title: 'Accept Service', desc: 'Agree on final price after negotiation' },
    { icon: CreditCard, title: 'Get Paid', desc: 'Receive payment minus 2.5% platform fee' },
    { icon: Star, title: 'Build Reputation', desc: 'Earn reviews and grow your business' }
  ];

  const faqs = [
    {
      q: i18n.language === 'es' ? '¿Cómo funciona el proceso de pago?' : 'How does the payment process work?',
      a: i18n.language === 'es' 
        ? 'Los clientes pagan por adelantado para reservar el servicio. El pago se procesa de forma segura a través de Wompi. Los proveedores reciben su pago menos una comisión de plataforma del 2.5%.'
        : 'Clients pay upfront to book the service. Payment is securely processed through Wompi. Providers receive their payment minus a 2.5% platform commission.'
    },
    {
      q: i18n.language === 'es' ? '¿Puedo negociar el precio?' : 'Can I negotiate the price?',
      a: i18n.language === 'es'
        ? 'Sí. Después de contactar a un proveedor, pueden chatear para discutir detalles y acordar el precio final antes de que el proveedor acepte el servicio.'
        : 'Yes. After contacting a provider, you can chat to discuss details and agree on the final price before the provider accepts the service.'
    },
    {
      q: i18n.language === 'es' ? '¿Qué pasa si no estoy satisfecho con el servicio?' : 'What if I\'m not satisfied with the service?',
      a: i18n.language === 'es'
        ? 'Ambas partes deben confirmar la finalización. Si hay un problema, no confirme y contacte nuestro soporte para resolver la disputa.'
        : 'Both parties must confirm completion. If there\'s an issue, don\'t confirm and contact our support to resolve the dispute.'
    },
    {
      q: i18n.language === 'es' ? '¿Cuál es la comisión de la plataforma?' : 'What is the platform commission?',
      a: i18n.language === 'es'
        ? 'La plataforma cobra una comisión del 2.5% sobre cada transacción. Esto ayuda a mantener la plataforma segura y confiable.'
        : 'The platform charges a 2.5% commission on each transaction. This helps maintain the platform secure and reliable.'
    },
    {
      q: i18n.language === 'es' ? '¿Cómo me convierto en proveedor?' : 'How do I become a provider?',
      a: i18n.language === 'es'
        ? 'Regístrese seleccionando "Proveedor de Servicios" como su tipo de cuenta. Complete su perfil, agregue sus servicios y comience a recibir solicitudes.'
        : 'Register by selecting "Service Provider" as your account type. Complete your profile, add your services, and start receiving requests.'
    },
    {
      q: i18n.language === 'es' ? '¿Es seguro mi pago?' : 'Is my payment secure?',
      a: i18n.language === 'es'
        ? 'Sí. Utilizamos Wompi, un procesador de pagos seguro y certificado, para manejar todas las transacciones.'
        : 'Yes. We use Wompi, a secure and certified payment processor, to handle all transactions.'
    },
    {
      q: i18n.language === 'es' ? '¿Puedo cancelar una reserva?' : 'Can I cancel a booking?',
      a: i18n.language === 'es'
        ? 'Puede cancelar antes del servicio contactando al proveedor a través del chat. Las políticas de reembolso se manejan caso por caso.'
        : 'You can cancel before the service by contacting the provider through chat. Refund policies are handled case by case.'
    },
    {
      q: i18n.language === 'es' ? '¿Cómo funciona el sistema de calificación?' : 'How does the rating system work?',
      a: i18n.language === 'es'
        ? 'Después de completar un servicio, los clientes pueden dejar una calificación de 1-5 estrellas y una reseña. Esto ayuda a mantener la calidad del servicio.'
        : 'After completing a service, clients can leave a 1-5 star rating and review. This helps maintain service quality.'
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <nav className="bg-white border-b">
        <div className="container mx-auto px-4 py-4">
          <Button variant="ghost" onClick={() => navigate('/')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('nav.home')}
          </Button>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-12 max-w-6xl">
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold mb-4" style={{ fontFamily: 'Outfit' }}>
            {i18n.language === 'es' ? 'Cómo Usar la Plataforma' : 'How to Use the Platform'}
          </h1>
          <p className="text-xl text-muted-foreground">
            {i18n.language === 'es' 
              ? 'Guía completa para clientes y proveedores'
              : 'Complete guide for clients and providers'}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-16">
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl" style={{ fontFamily: 'Outfit' }}>
                {i18n.language === 'es' ? 'Para Clientes' : 'For Clients'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {clientSteps.map((step, index) => (
                <div key={index} className="flex gap-4">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'hsl(215 100% 95%)' }}>
                    <step.icon className="w-6 h-6" style={{ color: 'hsl(215 100% 34%)' }} />
                  </div>
                  <div>
                    <div className="text-sm font-semibold mb-1">{index + 1}. {step.title}</div>
                    <div className="text-sm text-muted-foreground">{step.desc}</div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-2xl" style={{ fontFamily: 'Outfit' }}>
                {i18n.language === 'es' ? 'Para Proveedores' : 'For Providers'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {providerSteps.map((step, index) => (
                <div key={index} className="flex gap-4">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'hsl(48 100% 95%)' }}>
                    <step.icon className="w-6 h-6" style={{ color: 'hsl(48 100% 40%)' }} />
                  </div>
                  <div>
                    <div className="text-sm font-semibold mb-1">{index + 1}. {step.title}</div>
                    <div className="text-sm text-muted-foreground">{step.desc}</div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-3xl" style={{ fontFamily: 'Outfit' }}>
              {i18n.language === 'es' ? 'Preguntas Frecuentes' : 'Frequently Asked Questions'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`}>
                  <AccordionTrigger className="text-left">{faq.q}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </CardContent>
        </Card>

        <div className="text-center mt-12">
          <p className="text-muted-foreground mb-4">
            {i18n.language === 'es' ? '¿Todavía tienes preguntas?' : 'Still have questions?'}
          </p>
          <Button onClick={() => navigate('/contact')} size="lg">
            {i18n.language === 'es' ? 'Contactar Soporte' : 'Contact Support'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default HowToUsePage;
